/**
 * Thermal Printer helper — uses browser Web Serial API to talk directly to a USB ESC/POS printer.
 * Supported by Chrome/Edge desktop (requires HTTPS).
 * Fallback: window.print() through the classic HTML receipt template.
 */

// Store the last granted port on window so users don't have to re-select every time
let cachedPort = null;

const ESC = 0x1b, GS = 0x1d, LF = 0x0a;

// ESC/POS command builders
export const encoder = {
  init: () => new Uint8Array([ESC, 0x40]),
  align: (a) => new Uint8Array([ESC, 0x61, a === "center" ? 1 : a === "right" ? 2 : 0]),
  bold: (on) => new Uint8Array([ESC, 0x45, on ? 1 : 0]),
  size: (double) => new Uint8Array([GS, 0x21, double ? 0x11 : 0]),  // 0x11 = double W+H
  feed: (n = 1) => new Uint8Array(Array(n).fill(LF)),
  cut: () => new Uint8Array([GS, 0x56, 1]),
  text: (str) => new TextEncoder().encode(str),
};

const concat = (...arrs) => {
  const total = arrs.reduce((s, a) => s + a.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrs) { out.set(a, off); off += a.length; }
  return out;
};

export const isSupported = () => "serial" in navigator;

export async function connectPrinter() {
  if (!isSupported()) throw new Error("متصفحك لا يدعم Web Serial — استخدم Chrome/Edge على الكمبيوتر");
  if (cachedPort) return cachedPort;
  const port = await navigator.serial.requestPort();
  await port.open({ baudRate: 9600 });
  cachedPort = port;
  return port;
}

export async function writeBytes(bytes) {
  const port = await connectPrinter();
  const writer = port.writable.getWriter();
  await writer.write(bytes);
  writer.releaseLock();
}

/**
 * Print a formatted receipt.
 * @param {Object} receipt {header, lines, total, footer, currency}
 */
export async function printReceipt(receipt) {
  const { header = [], lines = [], total, footer = [], currency = "" } = receipt;
  const parts = [encoder.init()];

  // Header centered + bold
  parts.push(encoder.align("center"), encoder.bold(true), encoder.size(true));
  for (const h of header) { parts.push(encoder.text(h + "\n")); }
  parts.push(encoder.size(false), encoder.bold(false), encoder.feed(1));

  // Lines
  parts.push(encoder.align("left"));
  for (const line of lines) {
    if (typeof line === "string") {
      parts.push(encoder.text(line + "\n"));
    } else {
      const name = (line.name || "").padEnd(20).slice(0, 20);
      const qty = String(line.qty || "").padStart(4);
      const price = String(line.price || "").padStart(10);
      parts.push(encoder.text(`${name}${qty}${price}\n`));
    }
  }

  // Total
  if (total != null) {
    parts.push(encoder.feed(1), encoder.align("right"), encoder.bold(true), encoder.size(true));
    parts.push(encoder.text(`الإجمالي: ${Number(total).toLocaleString("ar-EG")} ${currency}\n`));
    parts.push(encoder.size(false), encoder.bold(false));
  }

  // Footer
  parts.push(encoder.align("center"), encoder.feed(1));
  for (const f of footer) parts.push(encoder.text(f + "\n"));

  parts.push(encoder.feed(3), encoder.cut());
  const bytes = concat(...parts);
  await writeBytes(bytes);
}

// ---------- Multi-printer support (one printer per kitchen station) ----------
// Each station keeps its own granted serial port so the cashier machine can
// route each station's ticket to its own USB printer.
const stationPorts = new Map();

export async function connectPrinterFor(key) {
  if (!isSupported()) throw new Error("متصفحك لا يدعم Web Serial — استخدم Chrome/Edge على الكمبيوتر");
  if (stationPorts.has(key)) return stationPorts.get(key);
  const port = await navigator.serial.requestPort();
  await port.open({ baudRate: 9600 });
  stationPorts.set(key, port);
  return port;
}

export function hasStationPort(key) { return stationPorts.has(key); }

export async function writeBytesTo(key, bytes) {
  const port = stationPorts.get(key) || await connectPrinterFor(key);
  const writer = port.writable.getWriter();
  await writer.write(bytes);
  writer.releaseLock();
}
